import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { GAME_TYPES, DIFFICULTIES, getAssociationFitAssessment } from '../../config/shared_config';
import { generateTaxonomyExercises } from '../../utils/taxonomyExerciseWorkflow';
import { getDefaultGameTypeForDomain } from '../../utils/taxonomyGenerationParams';

const SCHOOL_LEVELS = ['CP', 'CE1', 'CE2', 'CM1', 'CM2', '6ème'];

export default function TaxonomyGenerateModal({
  open,
  onClose,
  taxonomyTriplet,
  notionLabel,
  defaultGameType = '',
  onSuccess,
}) {
  const [gameType, setGameType] = useState(defaultGameType || 'speed_game');
  const [schoolLevel, setSchoolLevel] = useState('CE2');
  const [difficulty, setDifficulty] = useState('medium');
  const [count, setCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const gameTypeLabel = useMemo(() => {
    const found = Object.values(GAME_TYPES || {}).find((g) => g.id === gameType);
    return found?.label || gameType;
  }, [gameType]);

  const associationWarning = useMemo(() => {
    if (gameType !== 'association' || !taxonomyTriplet?.domain || !taxonomyTriplet?.competence) {
      return null;
    }
    const assessment = getAssociationFitAssessment(
      taxonomyTriplet.domain,
      taxonomyTriplet.competence,
      taxonomyTriplet.notion,
    );
    return assessment.fit ? null : assessment;
  }, [gameType, taxonomyTriplet]);

  React.useEffect(() => {
    if (open) {
      const domainDefault = taxonomyTriplet?.domain
        ? getDefaultGameTypeForDomain(taxonomyTriplet.domain)
        : 'speed_game';
      setGameType(defaultGameType || domainDefault);
      setError(null);
      setProgress(null);
    }
  }, [open, defaultGameType, taxonomyTriplet?.domain]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!taxonomyTriplet?.domain || !taxonomyTriplet?.competence || !taxonomyTriplet?.notion) {
      setError('Triplet taxonomique invalide');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await generateTaxonomyExercises({
        taxonomyTriplet,
        notionLabel: notionLabel || taxonomyTriplet.notion,
        gameType,
        gameTypeLabel,
        schoolLevel,
        difficulty,
        count,
        onProgress: setProgress,
      });
      onSuccess?.(result);
      onClose();
    } catch (err) {
      console.error('Erreur génération taxonomie:', err);
      setError(err?.message || 'Échec de la génération');
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const tripletLabel = taxonomyTriplet
    ? `${taxonomyTriplet.domain} / ${taxonomyTriplet.competence} / ${taxonomyTriplet.notion}`
    : '—';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Générer des exercices</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Crée un ou plusieurs exercices LLM pour combler un manque de couverture, puis ouvre le premier en relecture.
        </Typography>

        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Notion ciblée
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {notionLabel || taxonomyTriplet?.notion}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {tripletLabel}
          </Typography>
        </Box>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Type d&apos;exercice</InputLabel>
          <Select
            value={gameType}
            label="Type d'exercice"
            onChange={(e) => setGameType(e.target.value)}
            disabled={submitting}
          >
            {Object.values(GAME_TYPES || {}).map((game) => (
              <MenuItem key={game.id} value={game.id}>{game.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {associationWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {associationWarning.reason}
            {associationWarning.alternatives?.length > 0 && (
              <>
                {' '}
                Types recommandés :
                {' '}
                {associationWarning.alternatives.join(', ')}.
              </>
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Niveau</InputLabel>
            <Select
              value={schoolLevel}
              label="Niveau"
              onChange={(e) => setSchoolLevel(e.target.value)}
              disabled={submitting}
            >
              {SCHOOL_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Difficulté</InputLabel>
            <Select
              value={difficulty}
              label="Difficulté"
              onChange={(e) => setDifficulty(e.target.value)}
              disabled={submitting}
            >
              {Object.values(DIFFICULTIES || {}).map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel>Nombre à générer</InputLabel>
          <Select
            value={count}
            label="Nombre à générer"
            onChange={(e) => setCount(e.target.value)}
            disabled={submitting}
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {submitting && progress && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {progress.phase === 'save' ? 'Enregistrement' : 'Génération'}
              {' '}
              {progress.current}/{progress.total}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
            />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
        >
          Générer et relire
        </Button>
      </DialogActions>
    </Dialog>
  );
}
